/**
 * Group products into:
 *  - { type: 'full', product }
 *  - { type: 'multi-stack', front: { left,right,single,stackOrder,stackGroups }, rear: {...} }
 *
 * Logic:
 *  - Consecutive non-full products are accumulated into a "multi-stack" structure
 *    separated by stack type changes (wide => 'single', narrow => 'multi').
 *  - Full products flush any pending multi-stack.
 */
import { getProductTypeFromMetadata } from '../index';

const createEmptyMultiStack = () => ({
  front: { left: [], right: [], single: [], stackOrder: [] },
  rear: { left: [], right: [], single: [], stackOrder: [] }
});

const hasProductsInMultiStack = (multiStack) => {
  const sections = ['front', 'rear'];
  const bins = ['left', 'right', 'single'];
  return sections.some((section) => bins.some((bin) => multiStack[section][bin].length > 0)
  );
};

const initializeGroupingState = () => ({
  currentMultiStack: createEmptyMultiStack(),
  frontStackGroups: [],
  rearStackGroups: [],
  currentFrontGroup: null,
  currentRearGroup: null,
  lastFrontStackType: null,
  lastRearStackType: null
});

const flushMultiStack = (state, groups) => {
  if (!hasProductsInMultiStack(state.currentMultiStack)) return initializeGroupingState();

  const finalizedFrontGroups = state.currentFrontGroup
    ? [...state.frontStackGroups, state.currentFrontGroup]
    : state.frontStackGroups;

  const finalizedRearGroups = state.currentRearGroup
    ? [...state.rearStackGroups, state.currentRearGroup]
    : state.rearStackGroups;

  groups.push({
    type: 'multi-stack',
    front: {
      ...state.currentMultiStack.front,
      stackGroups: finalizedFrontGroups
    },
    rear: {
      ...state.currentMultiStack.rear,
      stackGroups: finalizedRearGroups
    }
  });

  return initializeGroupingState();
};

const getSectionInfo = (product) => {
  const isFront = product.Origin[0] >= 2;
  return { sectionKey: isFront ? 'front' : 'rear', isFront };
};

const deriveStackType = (productType) => (productType === 'wide' ? 'single' : 'multi');

const ensureGroupForStackType = (state, isFront, stackType) => {
  const lastType = isFront ? state.lastFrontStackType : state.lastRearStackType;
  const currentGroup = isFront ? state.currentFrontGroup : state.currentRearGroup;

  if (lastType === stackType && currentGroup) return state;

  const updatedFrontStackGroups = isFront && state.currentFrontGroup
    ? [...state.frontStackGroups, state.currentFrontGroup]
    : state.frontStackGroups;

  const updatedRearStackGroups = !isFront && state.currentRearGroup
    ? [...state.rearStackGroups, state.currentRearGroup]
    : state.rearStackGroups;

  const newGroup = { type: stackType, products: [] };

  return {
    ...state,
    frontStackGroups: updatedFrontStackGroups,
    rearStackGroups: updatedRearStackGroups,
    currentFrontGroup: isFront ? newGroup : state.currentFrontGroup,
    currentRearGroup: !isFront ? newGroup : state.currentRearGroup,
    lastFrontStackType: isFront ? stackType : state.lastFrontStackType,
    lastRearStackType: !isFront ? stackType : state.lastRearStackType
  };
};

const addStackOrderIfNeeded = (state, sectionKey, stackType) => {
  const order = state.currentMultiStack[sectionKey].stackOrder;
  if (order[order.length - 1] === stackType) return state;
  return {
    ...state,
    currentMultiStack: {
      ...state.currentMultiStack,
      [sectionKey]: {
        ...state.currentMultiStack[sectionKey],
        stackOrder: [...order, stackType]
      }
    }
  };
};

const addProductBins = (state, sectionKey, product, productType) => {
  const section = state.currentMultiStack[sectionKey];
  let newSection = section;

  if (productType === 'wide') {
    newSection = { ...section, single: [...section.single, product] };
  } else {
    const isLeft = product.Origin[1] < 2;
    newSection = {
      ...section,
      left: isLeft ? [...section.left, product] : section.left,
      right: !isLeft ? [...section.right, product] : section.right
    };
  }

  return {
    ...state,
    currentMultiStack: {
      ...state.currentMultiStack,
      [sectionKey]: newSection
    }
  };
};

const appendProductToGroup = (state, isFront, product) => {
  if (isFront && state.currentFrontGroup) {
    return {
      ...state,
      currentFrontGroup: {
        ...state.currentFrontGroup,
        products: [...state.currentFrontGroup.products, product]
      }
    };
  }
  if (!isFront && state.currentRearGroup) {
    return {
      ...state,
      currentRearGroup: {
        ...state.currentRearGroup,
        products: [...state.currentRearGroup.products, product]
      }
    };
  }
  return state;
};

const addProductToState = (state, product, productType) => {
  const { sectionKey, isFront } = getSectionInfo(product);
  const stackType = deriveStackType(productType);
  let next = ensureGroupForStackType(state, isFront, stackType);
  next = addStackOrderIfNeeded(next, sectionKey, stackType);
  next = addProductBins(next, sectionKey, product, productType);
  next = appendProductToGroup(next, isFront, product);
  return next;
};

export const groupProductsByType = (products, metadata) => {
  const groups = [];
  let state = initializeGroupingState();

  products.forEach((product) => {
    const productMetadata = metadata?.[product.choiceId]?.metadata;
    const productType = getProductTypeFromMetadata(productMetadata);

    if (productType === 'full') {
      state = flushMultiStack(state, groups);
      groups.push({ type: 'full', product });
    } else {
      state = addProductToState(state, product, productType);
    }
  });

  // reset state and flush any remaining multi-stack
  state = flushMultiStack(state, groups);
  return groups;
};
